import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { RubricCriteria, ScoringType } from '../../types/procurement';
import { type Column, StandardTable } from '../common/UI/StandardTable';

interface Props {
  initialCriteria?: RubricCriteria[];
  onSave: (criteria: RubricCriteria[]) => void;
  readOnly?: boolean;
}

const EMPTY_CRITERIA: RubricCriteria[] = [];

const EvaluationRubricBuilder: React.FC<Props> = ({
  initialCriteria = EMPTY_CRITERIA,
  onSave,
  readOnly = false,
}) => {
  const [criteria, setCriteria] = useState<RubricCriteria[]>(initialCriteria);

  useEffect(() => {
    setCriteria(initialCriteria);
  }, [initialCriteria]);

  const totalWeight = useMemo(() => {
    return criteria.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
  }, [criteria]);

  const handleAddCriteria = () => {
    setCriteria([
      ...criteria,
      {
        id: crypto.randomUUID(),
        category: '',
        name: '',
        description: '',
        weight: 0,
        scoringType: 'LINGUISTIC',
      },
    ]);
  };

  const handleRemoveCriteria = (id: string) => {
    setCriteria(criteria.filter((c) => c.id !== id));
  };

  const handleChange = (id: string, field: keyof RubricCriteria, value: string | number) => {
    setCriteria(
      criteria.map((c) => {
        if (c.id === id) {
          return { ...c, [field]: value };
        }
        return c;
      })
    );
  };

  const handleSave = () => {
    onSave(criteria);
  };

  const isWeightValid = totalWeight === 100;

  const columns: Column<RubricCriteria>[] = [
    {
      id: 'category',
      label: 'Category',
      width: '20%',
      render: (item) =>
        readOnly ? (
          <Typography variant="body2">{item.category}</Typography>
        ) : (
          <TextField
            fullWidth
            size="small"
            placeholder="e.g., Technical"
            value={item.category}
            onChange={(e) => handleChange(item.id, 'category', e.target.value)}
            data-testid={`category-input-${item.id}`}
          />
        ),
    },
    {
      id: 'name',
      label: 'Criteria Name',
      width: '25%',
      render: (item) =>
        readOnly ? (
          <Typography variant="body2">{item.name}</Typography>
        ) : (
          <TextField
            fullWidth
            size="small"
            placeholder="Criteria name"
            value={item.name}
            onChange={(e) => handleChange(item.id, 'name', e.target.value)}
            data-testid={`name-input-${item.id}`}
          />
        ),
    },
    {
      id: 'description',
      label: 'Description',
      width: '30%',
      render: (item) =>
        readOnly ? (
          <Typography variant="body2">{item.description}</Typography>
        ) : (
          <TextField
            fullWidth
            size="small"
            placeholder="Description"
            multiline
            maxRows={3}
            value={item.description}
            onChange={(e) => handleChange(item.id, 'description', e.target.value)}
            data-testid={`desc-input-${item.id}`}
          />
        ),
    },
    {
      id: 'scoringType',
      label: 'Scoring Type',
      width: '15%',
      render: (item) =>
        readOnly ? (
          <Typography variant="body2">
            {item.scoringType === 'LINGUISTIC'
              ? 'Linguistic'
              : item.scoringType === 'NUMERIC'
                ? 'Numeric'
                : 'Pass / Fail'}
          </Typography>
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            value={item.scoringType}
            onChange={(e) => handleChange(item.id, 'scoringType', e.target.value as ScoringType)}
            data-testid={`type-input-${item.id}`}
          >
            <MenuItem value="LINGUISTIC">Linguistic (Fuzzy)</MenuItem>
            <MenuItem value="NUMERIC">Numeric (0-10)</MenuItem>
            <MenuItem value="PASS_FAIL">Pass / Fail</MenuItem>
          </TextField>
        ),
    },
    {
      id: 'weight',
      label: 'Weight (%)',
      width: '10%',
      render: (item) =>
        readOnly ? (
          <Typography variant="body2">{item.weight}%</Typography>
        ) : (
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            value={item.weight}
            onChange={(e) => handleChange(item.id, 'weight', Number(e.target.value))}
            data-testid={`weight-input-${item.id}`}
          />
        ),
    },
    {
      id: 'actions',
      label: 'Actions',
      width: '5%',
      align: 'center',
      render: (item) =>
        readOnly ? null : (
          <IconButton
            color="error"
            onClick={() => handleRemoveCriteria(item.id)}
            data-testid={`delete-btn-${item.id}`}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        sx={{ bgcolor: 'action.hover' }}
        title="Evaluation Rubric Builder"
        subheader="Define the scoring criteria and weights for assessing supplier bids."
      />
      <CardContent>
        {!isWeightValid && criteria.length > 0 && (
          <Alert severity={totalWeight > 100 ? 'error' : 'warning'} sx={{ mb: 2 }}>
            Total weight must equal 100%. Current total: {totalWeight}%
          </Alert>
        )}

        <StandardTable
          columns={columns}
          data={criteria}
          keyExtractor={(item) => item.id}
          emptyStateMessage='No criteria defined. Click "Add Criteria" to begin.'
        />

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 2 }}>
            Total Weight:
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color={isWeightValid ? 'success.main' : 'error.main'}
            data-testid="total-weight"
            sx={{ mr: 2 }}
          >
            {totalWeight}%
          </Typography>
        </Box>

        {!readOnly && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCriteria}
              variant="outlined"
              data-testid="add-criteria-btn"
            >
              Add Criteria
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isWeightValid || criteria.length === 0}
              data-testid="save-rubric-btn"
            >
              Save Rubric
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EvaluationRubricBuilder;
